import React, { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"

import { useTranslation } from "react-i18next"

import { MultiDatesPicker } from "components/date/MultiDatesPicker"
import { InputToggle } from "components/inputs/InputToggle"

import { useGameSearch } from "hooks/useGameSearch"
import { useIsMounted } from "hooks/useIsMounted"

import { bonusEligibilityOptions, bonusTypeOptions, enumTenant, tenantOptions } from "constants/bonus"

import { BetbyBonusService, BetbyDbTemplate } from "services/betbyBonus/betbyBonus.service"
import { BonusService } from "services/bonus/bonus.service"
import { gameService } from "services/game/game.service"
import { GameInterface } from "services/game/type"
import { PaymentServiceAdmin } from "services/payment/payment.service"
import { settingsService } from "services/settings/settings.service"
import { AdminSettings } from "services/settings/type"
import { tagsService } from "services/tags/tags.service"

import { BonusEligibility, BonusType } from "types/api"

import { shallowChanged } from "util/shallowChanged"

import { formatFiatCurrency } from "../../util/formatter"
import { DropdownMenu } from "../inputs/DropdownMenu"
import { InputField } from "../inputs/InputField"

import { BonusBannerSection, BonusBannerState } from "./ModalBonus/BonusBannerSection"
import { createCMSBonusBanner } from "./ModalBonus/createCMSBonusBanner"
import { Modal } from "./Modal"

Yup.setLocale({
  mixed: {
    required: "Required Field"
  },
  number: {
    min: "Must be greater than or equal to ${min}"
  }
})

interface FormValuesType {
  type: BonusType
  title: string
  bonus_percent: number
  active: boolean
  days_to_expire: number
  wager_requirements: number
  max_usage_per_user: number
  max_bonus_amount: number
  min_deposit: number
  // zero_out_threshold: number
  // bet_max_limit: number
  // withdraw_max_limit: number
  submit?: any
  tenant_id: string
  banner_image: string
  schedule?: string[]
  schedule_timezone?: string
  currencies: {
    currency: string
    min_deposit: number
    max_bonus_amount: number
  }[]
  event: string
  freespinsAmount: number
  gameIdentifier: string
  pricePerSpin: number
  additional_bonus_id: string | null
  betby_template_id: string | null
  betby_freebet_amount_cents: number | null
  required_tag_id: string
  betby_freebet_currency: string | null
  eligibility: BonusEligibility
  max_cashout_amount: number | null
}

type ModalBonusProps = {
  open: boolean
  data: any
  handler: any
  saveId?: boolean
  forcedBonusType?: BonusType
}

type TagOption = { name: string; value: string }
const INITIAL_BANNER_STATE: BonusBannerState = { mode: "existing", newBannerName: "", newBannerImageUrl: "" }
const availableCurrenciesFallback = ["NOK", "EUR", "PLN", "USD", "CAD", "JPY", "BRL", "SEK", "TRY", "RUB", "PHP", "VND", "DKK", "CLP", "MXN"]

/** Format cents as a currency string using the correct symbol, e.g. 500 + "EUR" → "€5.00" */
function formatCurrencyAmount(cents: number, currency?: string | null): string {
  const cur = currency || "USD"
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(cents / 100)
  } catch {
    return `${cur} ${(cents / 100).toFixed(2)}`
  }
}

export const ModalBonus = ({ open, data, handler, saveId, forcedBonusType }: ModalBonusProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const type = data ? "edit" : "add"
  const isMounted = useIsMounted()
  const [fsBonuses, setFSBonuses] = useState<any[]>([])
  const [bannerState, setBannerState] = useState<BonusBannerState>(INITIAL_BANNER_STATE)
  const [bannerCreating, setBannerCreating] = useState(false)
  const [isBetbyFreebet, setIsBetbyFreebet] = useState(false)
  const [isBetbyDepositFreebet, setIsBetbyDepositFreebet] = useState(false)
  const [betbyTemplates, setBetbyTemplates] = useState<BetbyDbTemplate[]>([])
  const [betbyTemplatesLoading, setBetbyTemplatesLoading] = useState(false)
  const [availableTags, setAvailableTags] = useState<TagOption[]>([])
  const [tagsLoading, setTagsLoading] = useState(false)
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null)
  const [selectedGame, setSelectedGame] = useState<GameInterface | null>(null)
  const [gameSearchOpen, setGameSearchOpen] = useState(false)
  const {
    query: gameQuery,
    setQuery: setGameQuery,
    results: gameResults,
    loading: gameSearching,
    clear: clearGameSearch
  } = useGameSearch({ pageSize: 20 })

  // Fetch enabled BetBy templates lazily — only when a toggle is first enabled
  useEffect(() => {
    if (!isBetbyFreebet && !isBetbyDepositFreebet) return
    if (betbyTemplates.length > 0) return // already fetched
    setBetbyTemplatesLoading(true)
    BetbyBonusService.getTemplates(true)
      .then(setBetbyTemplates)
      .finally(() => setBetbyTemplatesLoading(false))
  }, [isBetbyFreebet, isBetbyDepositFreebet])

  const resolveBannerImage = useCallback(
    async (currentValue: string, tenantId: string, fallbackName: string): Promise<string | null> => {
      if (bannerState.mode !== "new" || !bannerState.newBannerImageUrl) return currentValue

      setBannerCreating(true)
      try {
        const created = await createCMSBonusBanner({
          tenantId,
          name: bannerState.newBannerName || fallbackName,
          imageUrl: bannerState.newBannerImageUrl
        })
        if (!created) {
          toast.error("Failed to create banner")
          return null
        }
        return created.id
      } catch {
        toast.error("Failed to create banner")
        return null
      } finally {
        setBannerCreating(false)
      }
    },
    [bannerState]
  )

  const loadData = async () => {
    setTagsLoading(true)
    try {
      const [bonuses, tags] = await Promise.all([
        BonusService.getBonusesList({
          page: 1,
          pageSize: 100,
          filters: { type: BonusType.FREESPINS },
          sortRow: "created_at",
          sortOrder: "desc"
        }),
        tagsService.getTags({ pageSize: 200 })
      ])
      setFSBonuses(bonuses?.data || [])
      if (tags?.data) {
        setAvailableTags(tags.data.map((t) => ({ name: t.name, value: t.id })))
      }
    } finally {
      setTagsLoading(false)
    }
  }

  useEffect(() => {
    if (gameResults.length > 0) setGameSearchOpen(true)
  }, [gameResults])

  useEffect(() => {
    if (open) {
      loadData()
      setIsBetbyFreebet(data?.type === BonusType.FREESPINS && !!data?.betby_template_id)
      setIsBetbyDepositFreebet(data?.type !== BonusType.FREESPINS && data?.type !== BonusType.FREEBET && !!data?.betby_template_id)
      setSelectedGame(null)
      clearGameSearch()
      if (data?.gameIdentifier) {
        gameService.getAllGames({ page: 1, pageSize: 1, gameIds: [data.gameIdentifier] }).then((result) => {
          const game = result?.games?.[0] as GameInterface | undefined
          if (game) setSelectedGame(game)
        })
      }
    }
  }, [open, data])

  useEffect(() => {
    settingsService.getAll().then((res) => {
      if (res) {
        setAdminSettings(res)
      }
    })
  }, [])

  const getInitialBonusType = (): BonusType => {
    if (saveId) return forcedBonusType ?? BonusType.PROMO
    return data?.type ?? ""
  }

  const formik = useFormik<FormValuesType>({
    initialValues: {
      type: getInitialBonusType(),
      title: data?.title ?? "",
      bonus_percent: data?.bonus_percent ?? 100,
      min_deposit: data?.min_deposit ?? 0,
      max_usage_per_user: data?.max_usage_per_user ?? 1,
      max_bonus_amount: data?.max_bonus_amount ?? 0,
      active: data?.active ?? false,
      days_to_expire: data?.days_to_expire ?? 7,
      wager_requirements: data?.wager_requirements ?? 10,
      tenant_id: data?.tenant_id ?? enumTenant.Blockbets,
      banner_image: data?.banner_image ?? "",
      schedule: data?.schedule ? data.schedule?.split(",") : [],
      schedule_timezone: data?.schedule_timezone ?? "",
      currencies: (() => {
        if (Array.isArray(data?.currencies)) return data.currencies
        try {
          return JSON.parse(data?.currencies) ?? []
        } catch {
          return []
        }
      })(),
      event: data?.event || "",
      freespinsAmount: data?.freespinsAmount ?? 0,
      gameIdentifier: data?.gameIdentifier ?? "",
      pricePerSpin: data?.pricePerSpin ?? 0,
      additional_bonus_id: data?.additional_bonus_id ?? null,
      betby_template_id: data?.betby_template_id ?? null,
      betby_freebet_amount_cents: data?.betby_freebet_amount_cents ?? null,
      required_tag_id: data?.required_tag_id ?? data?.required_tag?.id ?? "",
      betby_freebet_currency: data?.betby_freebet_currency ?? null,
      eligibility: data?.eligibility ?? BonusEligibility.ALL,
      max_cashout_amount: data?.max_cashout_amount ?? null
    },
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      try {
        if (type === "add") {
          const bannerImageValue = await resolveBannerImage(values.banner_image, values.tenant_id, values.title)
          if (bannerImageValue === null) return

          const newValues = {
            ...values,
            banner_image: bannerImageValue,
            schedule: values.schedule?.join(",")
          }
          if (newValues.additional_bonus_id === "") newValues.additional_bonus_id = null
          if (!newValues.betby_template_id) newValues.betby_template_id = null
          const response = await BonusService.addBonus(newValues)

          if (saveId && response?.id) {
            handler(response.id, values.title)
          } else {
            handler()
          }
          return toast.success("Bonus added successfully")
        }

        if (!formik.dirty && bannerState.mode !== "new") return

        const editBannerImage = await resolveBannerImage(values.banner_image, values.tenant_id, values.title)
        if (bannerState.mode === "new" && editBannerImage === null) return

        const changedValues = shallowChanged(values, formik.initialValues)
        const newValues = {
          ...changedValues,
          ...(editBannerImage && bannerState.mode === "new" && { banner_image: editBannerImage }),
          schedule: changedValues.schedule?.join(",")
        }
        if (newValues.additional_bonus_id === "") newValues.additional_bonus_id = null
        if (newValues.betby_template_id === "") newValues.betby_template_id = null
        await BonusService.editBonus(data.id, newValues)
        handler()
        return toast.success("Bonus edited successfully")
      } catch (err: any) {
        if (isMounted()) {
          helpers.setStatus({ success: false })
          // helpers.setErrors({ submit: err?.message ?? "Failed" })
          toast.error("Failed")
          helpers.setSubmitting(false)
        }
      }
    },
    validationSchema: Yup.object().shape({
      type: Yup.string().required(),
      title: Yup.string().required(),
      bonus_percent: Yup.number()
        .required()
        .when("type", {
          is: (val: string) => val === "FREESPINS" || val === "FREEBET",
          then: Yup.number().min(0).default(0),
          otherwise: Yup.number().min(1)
        }),
      min_deposit: Yup.number().required().min(0),
      max_usage_per_user: Yup.number().required().min(1),
      max_bonus_amount: Yup.number().required().min(0),
      active: Yup.boolean().required(),
      days_to_expire: Yup.number().required().min(0),
      wager_requirements: Yup.number()
        .required()
        .when("type", {
          is: "FREEBET",
          then: Yup.number().min(0),
          otherwise: Yup.number().min(1)
        }),
      tenant_id: Yup.mixed().oneOf([enumTenant.Slotwhales, enumTenant.Kingcasi, enumTenant.Blockbets]).required(),
      currencies: Yup.array()
        .of(
          Yup.object().shape({
            currency: Yup.string().required("Currency is required"),
            min_deposit: Yup.number().min(0).required("Min deposit required"),
            max_bonus_amount: Yup.number().min(0).required("Max bonus required")
          })
        )
        .default([]),
      event: Yup.string().optional().nullable().default(""),
      freespinsAmount: Yup.number().nullable().min(0).default(0),
      gameIdentifier: Yup.string().nullable().default(null),
      max_cashout_amount: Yup.number().nullable().min(0).default(null)
    })
  })

  // Load BetBy templates when FREEBET type is active (always shown, no toggle)
  useEffect(() => {
    if (formik.values.type !== BonusType.FREEBET) return
    if (betbyTemplates.length > 0) return
    setBetbyTemplatesLoading(true)
    BetbyBonusService.getTemplates(true)
      .then(setBetbyTemplates)
      .finally(() => setBetbyTemplatesLoading(false))
  }, [formik.values.type])

  const onGetCurRates = async (currency: string) => {
    const cRates = await PaymentServiceAdmin.getCurRates("USD")
    if (cRates?.rates && cRates.rates[currency]) {
      return cRates.rates[currency]
    }
    return 1
  }

  const addCurrency = () => {
    const updatedCurrencies = [
      ...formik.values.currencies,
      {
        currency: "",
        min_deposit: formik.values.min_deposit,
        max_bonus_amount: formik.values.max_bonus_amount
      }
    ]
    formik.setFieldValue("currencies", updatedCurrencies)
  }

  const removeCurrency = (index: number) => {
    const updatedCurrencies = formik.values.currencies.filter((_, i) => i !== index)
    formik.setFieldValue("currencies", updatedCurrencies)
  }

  const selectedBetbyTemplate = betbyTemplates.find((t) => t.betby_id === formik.values.betby_template_id) ?? null
  const betbyIsApiAmount = selectedBetbyTemplate?.freebet_data?.is_api_amount ?? false

  const updateCurrencyField = async (index: number, field: keyof FormValuesType["currencies"][0], value: any) => {
    const updatedCurrencies = [...formik.values.currencies]
    updatedCurrencies[index] = { ...updatedCurrencies[index], [field]: value }

    if (field === "currency" && value) {
      const exchangeRate = await onGetCurRates(value)
      updatedCurrencies[index].min_deposit = formik.values.min_deposit * exchangeRate
      updatedCurrencies[index].max_bonus_amount = formik.values.max_bonus_amount * exchangeRate
    }

    formik.setFieldValue("currencies", updatedCurrencies)
  }

  const availableCurrencies = adminSettings ? Object.entries(adminSettings.rcoin).map(([c]) => c) : availableCurrenciesFallback

  const resolvedBonusType = forcedBonusType ?? BonusType.PROMO
  const bonusTypeDropdownOptions = saveId ? [{ name: resolvedBonusType, value: resolvedBonusType }] : bonusTypeOptions

  const handleSelectGame = useCallback(
    (g: GameInterface) => {
      formik.setFieldValue("gameIdentifier", g.identifier)
      setSelectedGame(g)
      clearGameSearch()
      setGameSearchOpen(false)
    },
    [clearGameSearch, formik.setFieldValue]
  )

  const handleClearGame = useCallback(() => {
    formik.setFieldValue("gameIdentifier", "")
    setSelectedGame(null)
  }, [formik.setFieldValue])

  return (
    <Modal open={open} handler={handler}>
      <form noValidate onSubmit={formik.handleSubmit}>
        <div className="relative flex flex-col justify-between modal-box max-w-full">
          <p className="text-white pb-5">{t("bonus.general")}</p>
          <div className="flex flex-col overflow-y-auto -mx-[1.5rem] px-[1.5rem]">
            <div className="grid grid-cols-3 gap-2">
              <DropdownMenu
                id="tenant_id"
                title={t("bonus.tenant")}
                noAllValueText
                disableEmptyOption
                options={tenantOptions}
                value={formik.values.tenant_id}
                onChange={formik.handleChange}
                invalid={Boolean(formik.touched.tenant_id && formik.errors.tenant_id)}
                altLabelOne={formik.touched.tenant_id ? formik.errors.tenant_id : ""}
              />
              <DropdownMenu
                id="type"
                title={t("bonus.bonusType")}
                noAllValueText
                options={bonusTypeDropdownOptions}
                value={formik.values.type}
                onChange={formik.handleChange}
                invalid={Boolean(formik.touched.type && formik.errors.type)}
                altLabelOne={formik.touched.type ? formik.errors.type : ""}
              />
              <DropdownMenu
                id="eligibility"
                title="Eligibility"
                noAllValueText
                disableEmptyOption
                options={bonusEligibilityOptions}
                value={formik.values.eligibility}
                onChange={formik.handleChange}
                invalid={Boolean(formik.touched.eligibility && formik.errors.eligibility)}
                altLabelOne={formik.touched.eligibility ? formik.errors.eligibility : ""}
              />
              <InputField
                id="title"
                title={t("bonus.bonusTitle")}
                value={formik.values.title}
                onChange={formik.handleChange}
                invalid={Boolean(formik.touched.title && formik.errors.title)}
                altLabelTwo={formik.touched.title ? formik.errors.title : ""}
              />
              {formik.values.type !== "FREESPINS" && formik.values.type !== "FREEBET" && (
                <>
                  <InputField
                    type="number"
                    id="bonus_percent"
                    title={t("bonus.bonusPercent")}
                    value={formik.values.bonus_percent}
                    onChange={formik.handleChange}
                    invalid={Boolean(formik.touched.bonus_percent && formik.errors.bonus_percent)}
                    altLabelTwo={formik.touched.bonus_percent ? formik.errors.bonus_percent : ""}
                  />
                  <InputField
                    type="number"
                    id="min_deposit"
                    title={t("bonus.minDepositAmount")}
                    value={formik.values.min_deposit}
                    onChange={formik.handleChange}
                    altLabelOne={formatFiatCurrency(formik.values.min_deposit, "$0.00")}
                    invalid={Boolean(formik.touched.min_deposit && formik.errors.min_deposit)}
                    altLabelTwo={formik.touched.min_deposit ? formik.errors.min_deposit : ""}
                  />
                  <InputField
                    type="number"
                    id="max_bonus_amount"
                    title={t("bonus.maxBonusAmount")}
                    value={formik.values.max_bonus_amount}
                    onChange={formik.handleChange}
                    altLabelOne={formatFiatCurrency(formik.values.max_bonus_amount, "$0.00")}
                    invalid={Boolean(formik.touched.max_bonus_amount && formik.errors.max_bonus_amount)}
                    altLabelTwo={formik.touched.max_bonus_amount ? formik.errors.max_bonus_amount : ""}
                  />
                </>
              )}
              <InputField
                type="number"
                id="max_cashout_amount"
                title="Max cashout (leave empty for no cap)"
                value={formik.values.max_cashout_amount ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  formik.setFieldValue("max_cashout_amount", e.target.value === "" ? null : +e.target.value)
                }
                altLabelOne={
                  formik.values.max_cashout_amount !== null && formik.values.max_cashout_amount !== undefined
                    ? formatFiatCurrency(formik.values.max_cashout_amount, "$0.00")
                    : "No cashout limit"
                }
                invalid={Boolean(formik.touched.max_cashout_amount && formik.errors.max_cashout_amount)}
                altLabelTwo={formik.touched.max_cashout_amount ? formik.errors.max_cashout_amount : ""}
              />
              {formik.values.type !== "FREEBET" && (
                <InputField
                  type="number"
                  id="max_usage_per_user"
                  title="Max Usage Per User"
                  disabled
                  value={formik.values.max_usage_per_user}
                  onChange={formik.handleChange}
                  invalid={Boolean(formik.touched.max_usage_per_user && formik.errors.max_usage_per_user)}
                  altLabelTwo={formik.touched.max_usage_per_user ? formik.errors.max_usage_per_user : ""}
                />
              )}
              {formik.values.type !== "FREEBET" && (
                <InputField
                  type="number"
                  id="wager_requirements"
                  title="Wager Requirements"
                  value={formik.values.wager_requirements}
                  onChange={formik.handleChange}
                  invalid={Boolean(formik.touched.wager_requirements && formik.errors.wager_requirements)}
                  altLabelTwo={formik.touched.wager_requirements ? formik.errors.wager_requirements : ""}
                />
              )}
              <InputField
                type="number"
                id="days_to_expire"
                title={t("bonus.daysToExpire")}
                value={formik.values.days_to_expire}
                onChange={formik.handleChange}
                invalid={Boolean(formik.touched.days_to_expire && formik.errors.days_to_expire)}
                altLabelTwo={formik.touched.days_to_expire ? formik.errors.days_to_expire : ""}
              />
              {formik.values.type !== "FREEBET" && (
                <MultiDatesPicker
                  id="schedule"
                  title="Schedule"
                  value={formik.values.schedule}
                  onChange={formik.setFieldValue}
                  invalid={Boolean(formik.touched.schedule && formik.errors.schedule)}
                  altLabelTwo={formik.touched.schedule ? formik.errors.schedule : ""}
                  data={data}
                />
              )}
            </div>
            <div className="flex mt-4 items-end justify-start">
              <InputToggle id="active" title={t("bonus.active")} checked={formik.values.active} onChange={formik.handleChange} />
            </div>

            <div className="pt-4">
              <DropdownMenu
                title="Required Tag"
                placeholder="No tag required"
                options={availableTags}
                value={formik.values.required_tag_id}
                onChange={formik.handleChange}
                name="required_tag_id"
                id="required_tag_id"
                loading={tagsLoading}
              />
              {formik.values.required_tag_id && (
                <p className="text-xs text-yellow-400 mt-2">Only users with this tag will be eligible for this bonus.</p>
              )}
            </div>

            <BonusBannerSection
              tenantId={formik.values.tenant_id}
              bannerState={bannerState}
              onChange={setBannerState}
              selectedBannerId={formik.values.banner_image}
              onSelectedBannerChange={formik.handleChange}
              touched={Boolean(formik.touched.banner_image)}
              error={formik.errors.banner_image}
            />

            <div className="grid grid-cols-3 gap-2 pt-4">
              {formik.values.type === "FREESPINS" && (
                <>
                  <div className="col-span-3 flex items-center py-1">
                    <InputToggle
                      id="isBetbyFreebet"
                      title={t("bonus.betbyFreebet")}
                      checked={isBetbyFreebet}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setIsBetbyFreebet(e.target.checked)
                        if (!e.target.checked) {
                          formik.setFieldValue("betby_template_id", null)
                          formik.setFieldValue("betby_freebet_amount_cents", null)
                          formik.setFieldValue("betby_freebet_currency", null)
                        }
                      }}
                    />
                  </div>
                  {isBetbyFreebet ? (
                    <div className="col-span-3 flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-300">{t("bonus.betbyFreebetTemplate")}</label>
                      {betbyTemplatesLoading ? (
                        <p className="text-xs text-gray-400">{t("bonus.loadingTemplates")}</p>
                      ) : betbyTemplates.length === 0 ? (
                        <p className="text-xs text-yellow-400">{t("bonus.noTemplatesWarning")}</p>
                      ) : (
                        <>
                          <select
                            className="w-full px-3 py-2 text-sm rounded bg-gray-700 border border-gray-600 text-white"
                            value={formik.values.betby_template_id ?? ""}
                            onChange={(e) => formik.setFieldValue("betby_template_id", e.target.value || null)}
                          >
                            <option value="">— select a template —</option>
                            {betbyTemplates.map((t) => (
                              <option key={t.betby_id} value={t.betby_id}>
                                {t.name} —{" "}
                                {t.default_amount_cents !== null
                                  ? formatCurrencyAmount(t.default_amount_cents, t.default_currency)
                                  : "no default amount"}
                              </option>
                            ))}
                          </select>
                          {betbyIsApiAmount ? (
                            <div className="flex items-center gap-2 mt-1">
                              <label className="text-xs text-gray-400 whitespace-nowrap">
                                Amount (cents) <span className="text-red-400">*</span>:
                              </label>
                              <input
                                type="number"
                                min={1}
                                placeholder="e.g. 500 = $5.00"
                                className="w-full px-3 py-1.5 text-sm rounded bg-gray-700 border border-gray-600 text-white placeholder-gray-400"
                                value={formik.values.betby_freebet_amount_cents ?? ""}
                                onChange={(e) =>
                                  formik.setFieldValue("betby_freebet_amount_cents", e.target.value ? Number(e.target.value) : null)
                                }
                              />
                              <label className="text-xs text-gray-400 whitespace-nowrap">Currency:</label>
                              <select
                                className="px-2 py-1.5 text-sm rounded bg-gray-700 border border-gray-600 text-white"
                                value={formik.values.betby_freebet_currency ?? ""}
                                onChange={(e) => formik.setFieldValue("betby_freebet_currency", e.target.value || null)}
                              >
                                <option value="">— default —</option>
                                {availableCurrencies.map((c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ))}
                              </select>
                              {formik.values.betby_freebet_amount_cents ? (
                                <span className="text-xs text-green-400 whitespace-nowrap">
                                  {formatCurrencyAmount(formik.values.betby_freebet_amount_cents, formik.values.betby_freebet_currency)}
                                </span>
                              ) : (
                                <span className="text-xs text-red-400 whitespace-nowrap">required</span>
                              )}
                            </div>
                          ) : selectedBetbyTemplate ? (
                            <p className="text-xs text-gray-400 mt-1">ℹ️ Amount fixed by template</p>
                          ) : null}
                          {formik.values.betby_template_id && (
                            <p className="text-xs text-green-400">✓ Template ID: {formik.values.betby_template_id}</p>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <InputField
                        type="number"
                        id="pricePerSpin"
                        title={t("bonus.pricePerSpin")}
                        value={formik.values.pricePerSpin}
                        onChange={formik.handleChange}
                        invalid={Boolean(formik.touched.pricePerSpin && formik.errors.pricePerSpin)}
                        altLabelTwo={formik.touched.pricePerSpin ? formik.errors.pricePerSpin : ""}
                        altLabelOne={`$${formik.values.pricePerSpin / 100}`}
                      />
                      <InputField
                        type="number"
                        id="freespinsAmount"
                        title={t("bonus.freeSpinsAmount")}
                        value={formik.values.freespinsAmount}
                        onChange={formik.handleChange}
                        invalid={Boolean(formik.touched.freespinsAmount && formik.errors.freespinsAmount)}
                        altLabelTwo={formik.touched.freespinsAmount ? formik.errors.freespinsAmount : ""}
                      />
                      {formik.values.gameIdentifier ? (
                        <div className="w-full form-control">
                          <label className="label">
                            <span className="label-text">{t("bonus.gameIdentifier")}</span>
                          </label>
                          <div className="flex items-center gap-3 p-3 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#0C0C1A]">
                            {(selectedGame?.image_vertical || selectedGame?.image) && (
                              <img
                                src={selectedGame.image_vertical || selectedGame.image}
                                alt={selectedGame.title}
                                className="h-10 w-auto rounded flex-shrink-0"
                              />
                            )}
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className="text-sm font-medium truncate text-[rgba(6,7,21,1)] dark:text-white">
                                {selectedGame?.title ?? formik.values.gameIdentifier}
                              </span>
                              <span className="text-xs opacity-60 truncate text-[rgba(6,7,21,1)] dark:text-white">
                                {formik.values.gameIdentifier}
                              </span>
                            </div>
                            <button type="button" onClick={handleClearGame} className="btn btn-ghost btn-xs opacity-60 hover:opacity-100">
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="relative"
                          onBlur={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget as Node)) setGameSearchOpen(false)
                          }}
                        >
                          <InputField
                            title={t("bonus.gameIdentifier")}
                            value={gameQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameQuery(e.target.value)}
                            invalid={Boolean(formik.touched.gameIdentifier && formik.errors.gameIdentifier)}
                            altLabelTwo={formik.touched.gameIdentifier ? formik.errors.gameIdentifier : ""}
                          />
                          {gameSearchOpen && (
                            <div className="absolute z-10 mt-1 left-0 right-0 max-h-64 overflow-auto rounded-lg border border-black/10 dark:border-white/20 bg-white dark:bg-[#0C0C1A] shadow-lg">
                              {gameSearching && (
                                <div className="px-3 py-2 text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">Searching…</div>
                              )}
                              {!gameSearching && gameResults.length === 0 && gameQuery.trim().length >= 2 && (
                                <div className="px-3 py-2 text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">No matches.</div>
                              )}
                              {gameResults.map((g) => (
                                <button
                                  type="button"
                                  key={g.identifier}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleSelectGame(g)}
                                  className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-[rgba(132,_0,_168,_0.06)] dark:hover:bg-[rgba(132,_0,_168,_0.1)] text-[rgba(6,7,21,1)] dark:text-white"
                                >
                                  {(g.image_vertical || g.image) && (
                                    <img src={g.image_vertical || g.image} alt={g.title} className="h-10 w-auto rounded flex-shrink-0" />
                                  )}
                                  <div className="flex flex-col min-w-0">
                                    <span className="truncate text-sm">{g.title}</span>
                                    <span className="text-xs opacity-60 truncate">{g.gameProvider?.title ?? g.provider}</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <InputField
                        id="event"
                        title={t("bonus.event")}
                        value={formik.values.event}
                        onChange={formik.handleChange}
                        invalid={Boolean(formik.touched.event && formik.errors.event)}
                        altLabelTwo={formik.touched.event ? formik.errors.event : ""}
                      />
                    </>
                  )}
                </>
              )}

              {formik.values.type === "FREEBET" && (
                <>
                  <div className="col-span-3 flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-300">BetBy Freebet Template</label>
                    {betbyTemplatesLoading ? (
                      <p className="text-xs text-gray-400">Loading templates…</p>
                    ) : betbyTemplates.length === 0 ? (
                      <p className="text-xs text-yellow-400">
                        ⚠️ No enabled templates found. Sync and enable templates in the BetBy Bonuses page first.
                      </p>
                    ) : (
                      <>
                        <select
                          className="w-full px-3 py-2 text-sm rounded bg-gray-700 border border-gray-600 text-white"
                          value={formik.values.betby_template_id ?? ""}
                          onChange={(e) => formik.setFieldValue("betby_template_id", e.target.value || null)}
                        >
                          <option value="">— select a template —</option>
                          {betbyTemplates.map((t) => (
                            <option key={t.betby_id} value={t.betby_id}>
                              {t.name} —{" "}
                              {t.default_amount_cents !== null
                                ? formatCurrencyAmount(t.default_amount_cents, t.default_currency)
                                : "no default amount"}
                            </option>
                          ))}
                        </select>
                        {betbyIsApiAmount ? (
                          <div className="flex items-center gap-2 mt-1">
                            <label className="text-xs text-gray-400 whitespace-nowrap">
                              Amount (cents) <span className="text-red-400">*</span>:
                            </label>
                            <input
                              type="number"
                              min={1}
                              placeholder="e.g. 500 = $5.00"
                              className="w-full px-3 py-1.5 text-sm rounded bg-gray-700 border border-gray-600 text-white placeholder-gray-400"
                              value={formik.values.betby_freebet_amount_cents ?? ""}
                              onChange={(e) =>
                                formik.setFieldValue("betby_freebet_amount_cents", e.target.value ? Number(e.target.value) : null)
                              }
                            />
                            <label className="text-xs text-gray-400 whitespace-nowrap">Currency:</label>
                            <select
                              className="px-2 py-1.5 text-sm rounded bg-gray-700 border border-gray-600 text-white"
                              value={formik.values.betby_freebet_currency ?? ""}
                              onChange={(e) => formik.setFieldValue("betby_freebet_currency", e.target.value || null)}
                            >
                              <option value="">— default —</option>
                              {availableCurrencies.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                            {formik.values.betby_freebet_amount_cents ? (
                              <span className="text-xs text-green-400 whitespace-nowrap">
                                {formatCurrencyAmount(formik.values.betby_freebet_amount_cents, formik.values.betby_freebet_currency)}
                              </span>
                            ) : (
                              <span className="text-xs text-red-400 whitespace-nowrap">required</span>
                            )}
                          </div>
                        ) : selectedBetbyTemplate ? (
                          <p className="text-xs text-gray-400 mt-1">ℹ️ Amount fixed by template</p>
                        ) : null}
                        {formik.values.betby_template_id && (
                          <p className="text-xs text-green-400">✓ Template ID: {formik.values.betby_template_id}</p>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              {formik.values.type !== "FREESPINS" && formik.values.type !== "FREEBET" && (
                <>
                  <DropdownMenu
                    id="additional_bonus_id"
                    title={t("bonus.relatedBonus")}
                    noAllValueText
                    options={fsBonuses.map((item: any) => ({ name: item.title, value: item.id }))}
                    value={formik.values.additional_bonus_id}
                    onChange={formik.handleChange}
                    invalid={Boolean(formik.touched.additional_bonus_id && formik.errors.additional_bonus_id)}
                    altLabelOne={formik.touched.additional_bonus_id ? formik.errors.additional_bonus_id : ""}
                  />
                  <div className="col-span-3 flex items-center py-1">
                    <InputToggle
                      id="isBetbyDepositFreebet"
                      title={t("bonus.betbyFreebet")}
                      checked={isBetbyDepositFreebet}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setIsBetbyDepositFreebet(e.target.checked)
                        if (!e.target.checked) {
                          formik.setFieldValue("betby_template_id", null)
                          formik.setFieldValue("betby_freebet_amount_cents", null)
                          formik.setFieldValue("betby_freebet_currency", null)
                        }
                      }}
                    />
                  </div>
                  {isBetbyDepositFreebet && (
                    <div className="col-span-3 flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-300">{t("bonus.betbyFreebetTemplate")}</label>
                      {betbyTemplatesLoading ? (
                        <p className="text-xs text-gray-400">{t("bonus.loadingTemplates")}</p>
                      ) : betbyTemplates.length === 0 ? (
                        <p className="text-xs text-yellow-400">{t("bonus.noTemplatesWarning")}</p>
                      ) : (
                        <>
                          <select
                            className="w-full px-3 py-2 text-sm rounded bg-gray-700 border border-gray-600 text-white"
                            value={formik.values.betby_template_id ?? ""}
                            onChange={(e) => formik.setFieldValue("betby_template_id", e.target.value || null)}
                          >
                            <option value="">— none —</option>
                            {betbyTemplates.map((t) => (
                              <option key={t.betby_id} value={t.betby_id}>
                                {t.name} —{" "}
                                {t.default_amount_cents !== null
                                  ? formatCurrencyAmount(t.default_amount_cents, t.default_currency)
                                  : "no default amount"}
                              </option>
                            ))}
                          </select>
                          {betbyIsApiAmount ? (
                            <div className="flex items-center gap-2 mt-1">
                              <label className="text-xs text-gray-400 whitespace-nowrap">
                                Amount (cents) <span className="text-red-400">*</span>:
                              </label>
                              <input
                                type="number"
                                min={1}
                                placeholder="e.g. 500 = $5.00"
                                className="w-full px-3 py-1.5 text-sm rounded bg-gray-700 border border-gray-600 text-white placeholder-gray-400"
                                value={formik.values.betby_freebet_amount_cents ?? ""}
                                onChange={(e) =>
                                  formik.setFieldValue("betby_freebet_amount_cents", e.target.value ? Number(e.target.value) : null)
                                }
                              />
                              <label className="text-xs text-gray-400 whitespace-nowrap">Currency:</label>
                              <select
                                className="px-2 py-1.5 text-sm rounded bg-gray-700 border border-gray-600 text-white"
                                value={formik.values.betby_freebet_currency ?? ""}
                                onChange={(e) => formik.setFieldValue("betby_freebet_currency", e.target.value || null)}
                              >
                                <option value="">— default —</option>
                                {availableCurrencies.map((c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ))}
                              </select>
                              {formik.values.betby_freebet_amount_cents ? (
                                <span className="text-xs text-green-400 whitespace-nowrap">
                                  {formatCurrencyAmount(formik.values.betby_freebet_amount_cents, formik.values.betby_freebet_currency)}
                                </span>
                              ) : (
                                <span className="text-xs text-red-400 whitespace-nowrap">required</span>
                              )}
                            </div>
                          ) : selectedBetbyTemplate ? (
                            <p className="text-xs text-gray-400 mt-1">ℹ️ Amount fixed by template</p>
                          ) : null}
                          {formik.values.betby_template_id && (
                            <p className="text-xs text-green-400">✓ Template ID: {formik.values.betby_template_id}</p>
                          )}
                        </>
                      )}
                      <p className="text-xs text-yellow-400">
                        ⚠️ When set, completing this deposit bonus automatically issues a BetBy freebet.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            {formik.values.type !== "FREEBET" && (
              <div className="flex flex-col gap-4 pt-4">
                <div>
                  <p className="text-white">Currencies</p>
                  <div className="text-sm">Add a currency to make the bonus exclusive for specified currencies</div>
                </div>
                {(formik.values.currencies ?? []).map((currencyOption, index) => (
                  <div key={index} className="flex gap-4 mb-4">
                    <DropdownMenu
                      title="Currency"
                      options={availableCurrencies.map((curr) => ({ name: curr, value: curr }))}
                      value={currencyOption.currency}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCurrencyField(index, "currency", e.target.value)}
                    />
                    <InputField
                      type="number"
                      title="Min Deposit"
                      value={currencyOption.min_deposit}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCurrencyField(index, "min_deposit", +e.target.value)}
                      altLabelOne={
                        currencyOption.currency
                          ? `${currencyOption.min_deposit / 100} ${currencyOption.currency}`
                          : `$${formik.values.min_deposit / 100} USD`
                      }
                    />
                    <InputField
                      type="number"
                      title="Max Bonus"
                      value={currencyOption.max_bonus_amount}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCurrencyField(index, "max_bonus_amount", +e.target.value)}
                      altLabelOne={
                        currencyOption.currency
                          ? `${currencyOption.max_bonus_amount / 100} ${currencyOption.currency}`
                          : `$${formik.values.max_bonus_amount / 100} USD`
                      }
                    />
                    <button type="button" className="btn btn-danger self-end" onClick={() => removeCurrency(index)}>
                      Remove
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-primary bg-[rgba(169,78,206,1)] border-transparent hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent mt-2"
                  onClick={addCurrency}
                >
                  Add Currency
                </button>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 pt-8 pb-36"></div>
          </div>
          <div className="modal-action">
            <button
              className="btn btn-secondary bg-[#ce4e79] text-white border-transparent hover:border-transparent hover:bg-[#ce4e79c5]"
              onClick={() => handler()}
              type="button"
            >
              {t("common.cancel")}
            </button>
            <button
              className="btn btn-primary bg-[rgba(169,78,206,1)] border-transparent hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent"
              type="submit"
              disabled={formik.isSubmitting || bannerCreating}
            >
              {(formik.isSubmitting || bannerCreating) && <span className="loading loading-spinner" />}
              {type === "add" ? t("bonus.add") : t("common.save")}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
